import React from 'react';
import bemBlock from '@gik/core/utils/bemBlock';
import CalendarEventForm, { ICalendarEventFormValues } from './CalendarEventForm';
import { eventsApi } from '@gik/api/calendar';
import { Button, FormProps, LoadingSpinner, SvgIcon, UI } from '@gik/ui';
import { calendarEventFormValuesToPayload } from './utils';
import { ICalendarEntry, ICalendarEvent } from '@gik/calendar/models/Calendar';
import { deleteEvent, UpdateEventPayload } from '@gik/api/calendar/calendarEvents';
import noop from '@gik/core/utils/noop';
import { convertDateFromBackend } from '@gik/core/utils/l10n';
import { timeDataFormat } from '@gik/l10n';
import RRule, { rrulestr } from 'rrule';
import { timeToInt } from 'time-number';
import { moment } from '@gik/core/utils';
import { Moment } from 'moment';
import { useCalendarEventTypes } from '@gik/api/calendar/calendarEventType';
import { useInkindStore } from '@gik/inkind/store/InkindStore';
import { dateTimeFormat, isInPastAllTimezones } from '@gik/core/utils/DateTimeUtils';
import { openModifyClaimedEventConfirmation } from '../ModifyClaimedEventConfirmation';
import { openIntercomWindow } from '@gik/core/utils/Intercom';
import { useProducts } from '@gik/checkout';
import { useTranslation } from 'react-i18next';
import { translationKeys } from './i18n/en';
import { translationKeys as commonTranslationKeys } from '@gik/i18n/en/common';
import { useInkind } from '@gik/api/inkinds/inkind';
import { canUserEditPage } from '@gik/inkind-page/utils/inkindPagePermissions';
import { useCalendarStore } from '@gik/calendar/store/CalendarStore';
import { calendarEventMutatedAnalytics } from '@gik/calendar/utils/CalendarAnalytics';

export type CalendarEventFormEditMode = 'instance' | 'series';

export interface ICalendarEditEventFormProps extends FormProps {
  event: ICalendarEvent;
  entry: ICalendarEntry;
  buttonsPortal?: () => HTMLElement;
  onSuccess?: () => void;
  onDelete?: (response: boolean) => void;
}

export default function CalendarEditEventForm({
  buttonsPortal,
  onSuccess = noop,
  onDelete = noop,
  event,
  entry,
  className,
  ...otherProps
}: ICalendarEditEventFormProps): React.ReactElement {
  const bem = bemBlock('calendar-edit-event-form');
  const { t } = useTranslation();

  const { data: calendarEventTypes } = useCalendarEventTypes();
  const { data: giftCards } = useProducts(entry.allowedGiftCardIds);

  const allowedGiftCardIds = React.useMemo(() => entry.allowedGiftCardIds || [], [entry]);

  const [initialValues, setInitialValues] = React.useState<ICalendarEventFormValues>();

  const [errorMessage, setErrorMessage] = React.useState<string>();
  const [editMode, setEditMode] = React.useState<CalendarEventFormEditMode>();
  const [deleting, setDeleting] = React.useState<boolean>(false);
  const [startDate, setStartDate] = React.useState<Moment>();
  const [endDate, setEndDate] = React.useState<Moment>();
  const { hasChangedGiftCards } = useCalendarStore();

  const inkindRouteId = useInkindStore(state => state.inkindRouteId);
  const { data: inkind } = useInkind(inkindRouteId);

  const savingRef = React.useRef<boolean>(false);

  function setSaving(value: boolean) {
    savingRef.current = value;
  }

  React.useEffect(() => {
    setStartDate(convertDateFromBackend(editMode === 'instance' ? event.startsAt : entry.startsAt));
    setEndDate(entry.endsAt ? convertDateFromBackend(editMode === 'instance' ? event.endsAt : entry.endsAt) : null);
  }, [event, entry, editMode]);

  const isClaimed = !!entry.claim;
  const isPastEvent = isInPastAllTimezones(moment.utc(event.startsAt));

  const canEdit = isClaimed && canUserEditPage(inkind, entry.claim?.supporter.id);

  /**
   * populate initial values
   */
  React.useEffect(() => {
    const instanceStartTime = startDate?.isValid() ? startDate?.format(timeDataFormat) : null;
    const instanceEndTime = endDate?.isValid() ? endDate?.format(timeDataFormat) : null;

    let weekdays: boolean[];
    let repeat = -1;

    if (entry.repeat) {
      const rrule = rrulestr(entry.repeat.toString());

      if (rrule.options.freq === RRule.WEEKLY && rrule.origOptions.byweekday) {
        // custom repeat
        repeat = 10;
        weekdays = [false, false, false, false, false, false, false];
        rrule.options.byweekday.map(weekday => {
          weekdays[weekday] = true;
        });
      } else if (rrule.options.freq === RRule.DAILY && rrule.origOptions.interval === 2) {
        // every other day
        repeat = 11;
      } else {
        // normal repeat
        repeat = rrule.options.freq;
      }
    }

    const _initialValues: ICalendarEventFormValues = {
      title: entry.title,
      description: entry.description,
      repeat: editMode === 'instance' ? -1 : repeat,
      allDay: entry.allDay,
      startDate: startDate?.format(dateTimeFormat),
      endDate: endDate?.format(dateTimeFormat),
      startTime: instanceStartTime ? timeToInt(instanceStartTime) : null,
      endTime: instanceEndTime ? timeToInt(instanceEndTime) : null,
      allowGiftCards: entry.allowGiftCards,
      allowedServiceIds: entry.allowedServiceIds.concat([]),
      allowedGiftCardIds: allowedGiftCardIds.concat([]),
      numberRequired: entry.numberRequired,
      deliveryInstructions: entry.deliveryInstructions,
      emergencyContactInfo: entry.emergencyContactInfo,
      pickupLocation: entry.pickupLocation,
      dropoffLocation: entry.dropoffLocation,
      petCareTypeId: entry.petCareTypeId?.toString(),
      sendEmailToPageFollowers: false,
      weekdays,
    };

    setInitialValues(_initialValues);
  }, [allowedGiftCardIds, entry, editMode, endDate, startDate]);

  function updateEditMode(type: CalendarEventFormEditMode) {
    setEditMode(type);

    let dialogTitle = t(translationKeys.editDialogTitle);
    if (type === 'instance') {
      dialogTitle = t(translationKeys.editInstance);
    }
    if (type === 'series') {
      dialogTitle = t(translationKeys.editSeries);
    }
    // FIXME: better way to update the dialog title
    const titleEl = document.querySelector('.gik-calendar-edit-event .gik-modal-header__title');
    if (titleEl) {
      titleEl.innerHTML = dialogTitle;
    }
  }

  const isLoading = !calendarEventTypes || (!giftCards && allowedGiftCardIds?.length);

  if (isLoading) {
    return <LoadingSpinner center />;
  }

  // get selected eventType
  const eventType = calendarEventTypes?.find(eventType => eventType.id === entry.typeId);

  if (!eventType) {
    throw new Error(`EventType '${eventType}' not recognized`);
  }

  async function handleSubmit(values: ICalendarEventFormValues, giftCardsOnly: boolean) {
    if (savingRef.current) {
      return;
    }

    let noteToClaimOwner = null;

    // show a modification warning with a note to claim owner if this entry was already claimed
    // skip the notification if the owner is also the claimer
    if (entry.claim?.id && !canEdit) {
      noteToClaimOwner = await openModifyClaimedEventConfirmation({
        claimOwnerFirstName: entry.claim.supporter.firstName,
        claimOwnerLastName: entry.claim.supporter.lastName,
        modificationType: 'Edit',
      });
      if (!noteToClaimOwner) {
        return;
      }
    }

    const payload: UpdateEventPayload = {
      ...calendarEventFormValuesToPayload(values, giftCardsOnly),
      inkindRouteId,
      instanceDate: editMode === 'instance' ? event.startsAt : null,
      noteToClaimOwner,
    };

    setErrorMessage(null);
    setSaving(true);

    let response: boolean;

    try {
      response = await eventsApi.updateEvent(entry.id, payload);
    } catch (err) {
      console.error(err);
      if (err.exceptionType === 'System.IdentityModel.BadRequestException') {
        UI.notifyError(err.exceptionMessage);
      }
      setErrorMessage(err.message);
      setSaving(false);
      return;
    }

    if (response) {
      calendarEventMutatedAnalytics(
        'eventUpdated',
        entry.id,
        payload.allDay,
        payload.startsAt,
        payload.endsAt,
        payload.repeat,
        payload.description,
        payload.title,
        payload.typeId,
        calendarEventTypes.find(type => type.id === payload.typeId).name,
        {
          allowGiftCards: payload.allowGiftCards,
          giftCardsUpdated: hasChangedGiftCards,
          allowedGiftCardIds: payload.allowedGiftCardIds,
          deliveryInstructions: payload.deliveryInstructions,
          dropoffLocation: payload.dropoffLocation,
          emergencyContactInfo: payload.emergencyContactInfo,
          numberRequired: payload.numberRequired,
          petCareTypeId: payload.petCareTypeId,
          pickupLocation: payload.pickupLocation,
        }
      );
    }

    // don't set saving back to false so the save button will stay disabled
    // setSaving(false);
    onSuccess();
  }

  async function handleDelete() {
    let noteToClaimOwner = null;
    let shouldDelete;

    if (editMode === 'instance') {
      const editModeName = t(translationKeys.requestType);
      const dialogTitle = t(translationKeys.deleteRequestConfirmTitle, { type: editModeName });

      shouldDelete = await UI.confirm(
        t(translationKeys.deleteRequestConfirm, { type: editModeName.toLocaleLowerCase() }),
        {
          title: dialogTitle,
          okButtonProps: { variant: 'danger' },
          okText: t(commonTranslationKeys.delete),
        }
      );
    } else {
      if (entry.claim?.id && !canEdit) {
        noteToClaimOwner = await openModifyClaimedEventConfirmation({
          claimOwnerFirstName: entry.claim.supporter.firstName,
          claimOwnerLastName: entry.claim.supporter.lastName,
          modificationType: 'Delete',
        });
        shouldDelete = !!noteToClaimOwner;
      } else {
        const isSeries = entry.repeat != null && entry.repeat != '';
        const editModeName = isSeries ? t(translationKeys.seriesType) : t(translationKeys.requestType);

        shouldDelete = await UI.confirm(
          t(translationKeys.deleteRequestConfirm, {
            type: editModeName.toLocaleLowerCase(),
          }),
          {
            title: t(translationKeys.deleteRequestConfirmTitle, { type: editModeName }),
            okButtonProps: { variant: 'danger' },
            okText: t(commonTranslationKeys.delete),
          }
        );
      }
    }
    if (shouldDelete) {
      let response: boolean;
      setDeleting(true);
      try {
        response = await deleteEvent(entry.id, {
          noteToClaimOwner,
          instanceDate: editMode === 'instance' ? event.startsAt : null,
        });
      } catch (err) {
        console.error(err);
        if (err.exceptionType === 'System.IdentityModel.BadRequestException') {
          UI.notifyError(err.exceptionMessage);
        }
        setDeleting(false);
        return;
      }
      setDeleting(false);
      onDelete(response);
    }
  }

  function handleOpenIntercom() {
    openIntercomWindow();
  }

  // re-order filteredProducts so it has the same order as allowedGiftCardIds
  const giftCardsSorted = allowedGiftCardIds
    .map(id => {
      return giftCards.find(item => item.id === id);
    })
    .filter(item => item); // remove null entries

  // choose editMode if this is a repeated event
  if (entry && entry.repeat && editMode === undefined) {
    return (
      <div className={bem('edit-choice', null, className)}>
        <div className="close-btn" onClick={close}></div>

        <Button onClick={() => updateEditMode('series')}>{t(translationKeys.editSeries)}</Button>
        <p>{t(translationKeys.editSeriesDescription)}</p>
        <hr />
        <Button onClick={() => updateEditMode('instance')}>{t(translationKeys.editInstance)}</Button>
        <p>{t(translationKeys.editInstanceDescription)}</p>
      </div>
    );
  }

  return (
    <div className={bem(null, null, className)}>
      <CalendarEventForm
        isClaimed={isClaimed}
        typeId={entry.typeId}
        errorMessage={errorMessage}
        initialValues={{ ...initialValues }}
        giftCards={giftCardsSorted}
        buttonsPortal={buttonsPortal}
        buttons={({ form, submitting }) => {
          const isSubmitting = submitting || savingRef.current || deleting;
          return (
            <div className={'gik-form__actions-new'}>
              <section>
                <Button variant={'default-light'} onClick={handleOpenIntercom} circle>
                  <SvgIcon name={'InterrogationRed'} set={'gik-icons'} />
                </Button>
              </section>
              <section>
                <Button
                  onClick={form.submit}
                  loading={savingRef.current}
                  disabled={isSubmitting}
                  preventClickWhenDisabled
                >
                  {t(commonTranslationKeys.save)}
                </Button>
                {!(isPastEvent && isClaimed) && (
                  <Button
                    variant="danger"
                    onClick={() => handleDelete()}
                    loading={deleting}
                    disabled={isSubmitting}
                    preventClickWhenDisabled
                    prepend={<SvgIcon name="Trash" />}
                    hideLabelOnMobile
                  >
                    {t(commonTranslationKeys.delete)}
                  </Button>
                )}
              </section>
            </div>
          );
        }}
        {...otherProps}
        onSubmitForm={handleSubmit}
      />
    </div>
  );
}
